import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

// Types for database search
export interface DatabaseSearchResult {
  id: string
  brandId: string
  name: string
  description?: string
  strengthLevel?: string
  wrapperType?: string
  binderType?: string
  fillerType?: string
  vitola?: {
    id: string
    name: string
  }
  brand: {
    id: string
    name: string
    country?: string
    imageUrl?: string
  }
  averageRating: number | null
  totalReviews: number
  totalSmoked: number
  averagePrice: number | null
}

export interface DatabaseSearchParams {
  search?: string
  brandId?: string
  vitolaId?: string
  strength?: string
  wrapperType?: string
  origin?: string
  minPrice?: number
  maxPrice?: number
  sortBy?: 'relevance' | 'rating' | 'newest' | 'name'
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface DatabaseSearchResponse {
  results: DatabaseSearchResult[]
  pagination: {
    page: number
    limit: number
    totalCount: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export interface AutocompleteSuggestion {
  type: 'brand' | 'cigarLine' | 'vitola'
  id: string
  name: string
}

export interface CigarLineDetail {
  id: string
  brandId: string
  name: string
  description?: string
  strengthLevel?: string
  wrapperType?: string
  binderType?: string
  fillerType?: string
  brand: {
    id: string
    name: string
    country?: string
  }
  averageRating: number | null
  totalReviews: number
  totalSmoked: number
  totalWishlist: number
}

export interface SimilarCigarResult {
  reference: {
    id: string
    name: string
    brand: { id: string; name: string }
  }
  similar: DatabaseSearchResult[]
}

export interface SavedSearch {
  id: string
  userId: string
  name: string
  query?: string
  filters?: Record<string, unknown>
  sortBy?: string
  alertEnabled: boolean
  lastAlertAt?: string
  createdAt: string
  updatedAt: string
}

export interface CreateSavedSearchRequest {
  name: string
  query?: string
  filters?: Record<string, unknown>
  sortBy?: string
  alertEnabled?: boolean
}

export const databaseApi = createApi({
  reducerPath: 'databaseApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/database',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['DatabaseSearch', 'SavedSearch'],
  endpoints: (builder) => ({
    searchDatabase: builder.query<DatabaseSearchResponse, DatabaseSearchParams>({
      query: (params) => ({
        url: '/search',
        params,
      }),
      transformResponse: (response: { success: boolean; data: DatabaseSearchResponse }) => response.data,
      providesTags: ['DatabaseSearch'],
    }),

    getAutocomplete: builder.query<AutocompleteSuggestion[], { q: string; type?: string }>({
      query: (params) => ({
        url: '/autocomplete',
        params,
      }),
      transformResponse: (response: { success: boolean; data: { suggestions: AutocompleteSuggestion[] } }) =>
        response.data.suggestions,
    }),

    getCigarLineDetail: builder.query<CigarLineDetail, string>({
      query: (id) => `/cigar-lines/${id}`,
      transformResponse: (response: { success: boolean; data: { cigarLine: CigarLineDetail } }) =>
        response.data.cigarLine,
    }),

    getSimilarCigars: builder.query<SimilarCigarResult, string>({
      query: (id) => `/similar/${id}`,
      transformResponse: (response: { success: boolean; data: SimilarCigarResult }) => response.data,
    }),

    getSavedSearches: builder.query<SavedSearch[], void>({
      query: () => '/saved-searches',
      transformResponse: (response: { success: boolean; data: { savedSearches: SavedSearch[] } }) =>
        response.data.savedSearches,
      providesTags: ['SavedSearch'],
    }),

    createSavedSearch: builder.mutation<SavedSearch, CreateSavedSearchRequest>({
      query: (body) => ({
        url: '/saved-searches',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: { savedSearch: SavedSearch } }) =>
        response.data.savedSearch,
      invalidatesTags: ['SavedSearch'],
    }),

    deleteSavedSearch: builder.mutation<void, string>({
      query: (id) => ({
        url: `/saved-searches/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SavedSearch'],
    }),
  }),
})

export const {
  useSearchDatabaseQuery,
  useGetAutocompleteQuery,
  useGetCigarLineDetailQuery,
  useGetSimilarCigarsQuery,
  useGetSavedSearchesQuery,
  useCreateSavedSearchMutation,
  useDeleteSavedSearchMutation,
} = databaseApi
